import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import BrandLogo from './BrandLogo';
import '../css/PosSidebar.css';

/**
 * The menu drawer, opened by the top bar's Menu button.
 *
 * ONE component for both /admin and every client's till. They differ only
 * in what they are branded as and which screens they list, so they take the
 * same drawer — a second implementation is how the two end up behaving
 * differently on a phone.
 *
 * A DRAWER, not a fixed rail, following the court-booking system: the till's
 * screens are wide (a product grid, a sales table, a receipt preview) and a
 * permanent 248px column costs them a quarter of a counter tablet all day to
 * show a menu nobody is reading between customers.
 *
 * Each item carries a one-line description, so the menu answers "which screen
 * do I want" rather than only listing nouns.
 *
 * IT WEARS THE CLUB'S OWN COLOURS. The panel takes Theme -> Screen Background
 * and Theme -> Text Color rather than the card surface, so the menu is part of
 * the till rather than a white sheet laid over it. The edge that separates the
 * two is the border and the shadow, not a difference in colour.
 *
 * NOT CLOSED BY A BACKDROP TAP (CLAUDE.md > Behavior). The × closes it, so
 * does Escape, and so does choosing a screen. A counter is exactly where a
 * stray tap happens.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.brandName - the club, or the product on /admin.
 * @param {string} [props.logoUrl]
 * @param {Array<{key: string, label: string, description?: string}>} props.tabs
 * @param {string} props.activeTab
 * @param {(key: string) => void} props.onTabChange
 * @param {string} props.signedInEmail
 * @param {() => void} props.onSignOut
 * @param {string} [props.navLabel] - accessible name for the menu.
 * @returns {?JSX.Element}
 */
function PosSidebar({
  open,
  onClose,
  brandName,
  logoUrl = '',
  tabs,
  activeTab,
  onTabChange,
  signedInEmail,
  onSignOut,
  navLabel = 'Screens',
}) {
  // Escape closes it, and the page behind stops scrolling while it is up —
  // without that, scrolling the drawer to its last item scrolls the till
  // underneath instead once the list runs out.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleClick = (key) => {
    onTabChange(key);
    onClose();
  };


  return createPortal(
    <div className="pos-drawer-overlay">
      {/* Dims the screen and swallows clicks meant for what is behind, but
          does NOT close — see the note above. */}
      <div className="pos-drawer-backdrop" />

      <aside className="pos-drawer" role="dialog" aria-modal="true" aria-label={navLabel}>
        <div className="pos-drawer-head">
          <div className="pos-drawer-brand">
            <BrandLogo src={logoUrl} alt={brandName} className="pos-drawer-logo" />
            <span className="pos-drawer-name" title={brandName}>{brandName}</span>
          </div>
          <button type="button" className="pos-drawer-close" onClick={onClose} aria-label="Close menu">
            &times;
          </button>
        </div>

        <nav className="pos-drawer-nav" aria-label={navLabel}>
          <p className="pos-drawer-label">Menu</p>
          {tabs.map(({ key, label, description }) => (
            <button
              key={key}
              type="button"
              className={`pos-drawer-link${activeTab === key ? ' active' : ''}`}
              aria-current={activeTab === key ? 'page' : undefined}
              onClick={() => handleClick(key)}
            >
              <span className="pos-drawer-link-label">{label}</span>
              {description && <span className="pos-drawer-link-desc">{description}</span>}
            </button>
          ))}
        </nav>

        <div className="pos-drawer-foot">
          {/* Labelled: an address on its own at the bottom of a menu is a fact
              without a question, and "who am I signed in as" is the question
              someone opening this is actually asking. */}
          <span className="pos-drawer-who">Signed in as</span>
          <span className="pos-drawer-user" title={signedInEmail}>{signedInEmail}</span>
          <button type="button" className="pos-drawer-signout" onClick={onSignOut}>Logout</button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

export default PosSidebar;
